import { useQueryClient } from '@tanstack/react-query';

import {
  discoveryQueryKeys,
  executeDiscoverySearch,
  type ExecuteDiscoverySearchRequest,
} from '@/entities/discovery';
import { useAbortableMutation } from '@/shared/hooks/use-abortable-mutation';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';

import { searchCreatorPendingAtom } from '../stores/mutation-atom';
import { useDiscoveryStore } from '../stores/discovery-store';

type ExecuteDiscoverySearchMutationRequest = {
  discoverySessionId: string;
  request: ExecuteDiscoverySearchRequest;
};

export function useExecuteDiscoverySearch() {
  const { setSession } = useDiscoveryStore();
  const queryClient = useQueryClient();
  const { workspaceId } = useWorkspaceId();

  return useAbortableMutation({
    mutationFn: ({ discoverySessionId, request }: ExecuteDiscoverySearchMutationRequest, signal) =>
      executeDiscoverySearch(discoverySessionId, request, signal),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: discoveryQueryKeys.searches(workspaceId) });
    },
    pendingAtom: searchCreatorPendingAtom,
    requestKey: 'discovery:execute-search',
    onSuccess: (response, variables) => {
      setSession((currentSession) => {
        if (!currentSession) return null;

        return {
          ...currentSession,
          current_search_history_id: response.search_id,
        };
      });
      queryClient.invalidateQueries({
        queryKey: discoveryQueryKeys.currentSession(workspaceId),
      });
      queryClient.invalidateQueries({
        queryKey: discoveryQueryKeys.searchStatus(
          workspaceId,
          variables.discoverySessionId,
          response.search_id,
        ),
      });
    },
  });
}
